import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getAllEmployees } from "../../../services/chatServices";
import { createPortal } from "react-dom";
import AppIcon from "../../../components/ui/AppIcon";

export default function DirectChatUserPicker({ onClose }) {
  const { directMessages } = useChat();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const itemRefs = useRef([]);
  const handleConfirmRef = useRef(null);

  const currentUserId = user?._id || user?.id;

  useEffect(() => {
    if (!currentUserId) return;
    setLoadingUsers(true);
    getAllEmployees(currentUserId)
      .then((res) => {
        const data = res?.data || [];
        const users = data.map((item) => ({
          _id: item.otherUserId,
          ...(item.otherUserData || {}),
        }));
        setAllUsers(users);
      })
      .catch((err) => {
        console.error("DirectChatUserPicker: failed to load users", err);
        setAllUsers([]);
      })
      .finally(() => setLoadingUsers(false));
  }, [currentUserId]);

  const existingDMIds = useMemo(
    () => new Set(directMessages.map((dm) => String(dm._id || dm.id))),
    [directMessages],
  );

  const filtered = useMemo(() => {
    return allUsers.filter((u) => {
      const uid = String(u._id || u.id);
      if (uid === String(currentUserId)) return false;
      if (!query.trim()) return true;
      return (u.name || u.displayName || u.username || "")
        .toLowerCase()
        .includes(query.toLowerCase());
    });
  }, [allUsers, query, currentUserId]);

  const handleConfirm = () => {
    if (!selectedUser) return;
    const targetId = selectedUser._id || selectedUser.id;
    navigate(`/dm/${targetId}`);
    onClose();
  };

  handleConfirmRef.current = handleConfirm;

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          filtered.length === 0 ? -1 : Math.min(prev + 1, filtered.length - 1)
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedIndex >= 0 && filtered[selectedIndex]) {
          const user = filtered[selectedIndex];
          setSelectedUser(user);
          if (selectedUser && (selectedUser._id || selectedUser.id) === (user._id || user.id)) {
            handleConfirmRef.current?.();
          }
        }
      }
    },
    [onClose, filtered, selectedIndex],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setSelectedIndex(-1);
    itemRefs.current = [];
  }, [filtered]);

  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl p-4 animate-fade-in">
      <div
        className="w-full max-w-lg bg-app-surface rounded-3xl shadow-2xl overflow-hidden animate-fly-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle bg-app-bg/20">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-xl opacity-20 blur-lg" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                  <AppIcon name="ChatBubbleLeftRightIcon" size={18} className="text-white" />
                </div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-text-primary tracking-tight">
                  New Message
                </h2>
                <p className="text-xs text-text-muted">
                  Select a user to start chatting
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 transition-all"
            >
              <AppIcon name="XMarkIcon" size={20} />
            </button>
          </div>

          <div className="px-6 pt-5">
            <div className="relative group">
              <AppIcon
                name="MagnifyingGlassIcon"
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-primary transition-colors"
              />
              <input
                autoFocus
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedUser(null);
                }}
                placeholder="Search users..."
                className="w-full bg-app-bg border border-border-subtle rounded-2xl pl-11 pr-4 py-3.5 text-sm text-text-primary outline-none focus:border-primary/50 transition-all placeholder:text-text-muted/40 shadow-subtle focus:ring-4 focus:ring-primary/5"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar max-h-[350px]">
            {loadingUsers ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                  <AppIcon name="UserIcon" size={24} className="text-primary/50" />
                </div>
                <p className="text-sm font-semibold text-text-primary mb-1">No users found</p>
                <p className="text-xs text-text-muted">Try a different search term</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map((u, index) => {
                  const uid = u._id || u.id;
                  const isSelected =
                    (selectedUser && (selectedUser._id || selectedUser.id) === uid) ||
                    index === selectedIndex;
                  const hasExistingDM = existingDMIds.has(String(uid));

                  return (
                    <div
                      key={uid}
                      ref={(el) => (itemRefs.current[index] = el)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => { setSelectedUser(u); setSelectedIndex(index); }}
                      role="option"
                      aria-selected={isSelected}
                      className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer border transition-all duration-200 ${
                        isSelected
                          ? "bg-primary/10 border-primary/30 shadow-xl shadow-primary/5"
                          : "bg-app-bg/50 border-border-subtle hover:bg-primary/5 hover:border-primary/20 hover:shadow-md"
                      }`}
                    >
                      <div className="relative flex-shrink-0">
                        <img
                          src={
                            u.profilePhoto ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              u.name || "User",
                            )}`
                          }
                          alt={u.name}
                          className="w-11 h-11 rounded-xl object-cover border-2 border-app-surface shadow-md"
                        />
                        {isSelected && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-app-surface">
                            <AppIcon name="CheckIcon" size={10} className="text-white" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-text-primary truncate">
                          {u.name || u.displayName || u.username}
                        </p>
                        <p className="text-xs text-text-muted truncate mt-0.5">
                          {u.email}
                        </p>
                      </div>

                      {hasExistingDM && (
                        <span className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-success/10 text-success border border-success/20 flex items-center gap-1">
                          <AppIcon name="ChatBubbleLeftRightIcon" size={10} />
                          Existing
                        </span>
                      )}

                      {u.user_role && !hasExistingDM && (
                        <span className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                          {typeof u.user_role === 'object' ? u.user_role.role : u.user_role}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-border-subtle p-5 flex gap-3 bg-app-bg/20">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-border-subtle text-text-muted hover:text-text-primary hover:bg-black/5 text-sm font-medium transition-all"
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              disabled={!selectedUser}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95 ${
                !selectedUser
                  ? "bg-primary/20 text-primary/50 cursor-not-allowed"
                  : "bg-primary text-white shadow-primary/20 hover:bg-primary/90 hover:shadow-primary/30"
              }`}
            >
              <AppIcon name="PaperAirplaneIcon" size={16} />
              {selectedUser &&
              existingDMIds.has(String(selectedUser._id || selectedUser.id))
                ? "Open Chat"
                : "Start Chat"}
            </button>
          </div>

          <div className="px-6 py-4 bg-app-bg/10 border-t border-border-subtle flex items-center gap-4 text-xs font-medium text-text-muted">
            <span className="flex items-center gap-2">
              <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-2">
              <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">Enter</kbd>
              Select
            </span>
            <span className="flex items-center gap-2">
              <kbd className="px-2.5 py-1.5 bg-app-surface rounded-lg border border-border-subtle text-text-primary font-semibold text-[10px]">Esc</kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}